<script lang="ts" setup>
import { useLanguage } from '#composables/useLanguage';
import { type DateRangeType, type PeriodGranularityType } from '#domain/core/filter/period';
import { getLocale } from '#i18n';
import { VueDatePicker, type ModelValue } from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { computed } from 'vue';
import type { RangeCalendarsPropsType } from './types/rangeCalendarsProps.type';
import { fromPickerModel, toPickerModel } from './utils/rangeCalendars.utils';

const { name, granularity, modelValue, minDate, maxDate, disabled = false } = defineProps<RangeCalendarsPropsType>();

const emit = defineEmits<{
  'update:modelValue': [range: DateRangeType];
}>();

const { language } = useLanguage();

// getLocale() reads i18next directly and is not reactive on its own, so read language.value to track it here.
const locale = computed(() => {
  void language.value;
  return getLocale();
});

/** vue-datepicker mode per granularity: the user can only pick whole units of the current granularity. */
const MODE_PROPS = {
  daily: { range: true, multiCalendars: true },
  weekly: { weekPicker: true, range: true, weekNumbers: { type: 'iso' }, multiCalendars: true },
  monthly: { monthPicker: true, range: true, multiCalendars: true }
} as const satisfies Record<PeriodGranularityType, object>;

const modeProps = computed(() => MODE_PROPS[granularity]);
// vue-datepicker's own ModelValue type does not cover the nested week-range shape it accepts at runtime
const pickerModel = computed(() => toPickerModel(modelValue, granularity) as ModelValue);
// Open the calendar on the selected period, or on the most recent available data
const startDate = computed(() => (modelValue?.end ?? maxDate)?.toDate());

function onUpdate(model: unknown) {
  const range = fromPickerModel(model, granularity);

  if (range) {
    emit('update:modelValue', range);
  }
}
</script>

<template>
  <!-- Size container for the @container rule below: the calendars shrink their text in a narrow drawer -->
  <div class="period-range-calendars-container">
    <VueDatePicker
      v-bind="modeProps"
      class="period-range-calendars"
      :model-value="pickerModel"
      :name="name"
      :disabled="disabled"
      :min-date="minDate?.toDate()"
      :max-date="maxDate?.toDate()"
      :start-date="startDate"
      :locale="locale"
      :time-config="{ enableTimePicker: false }"
      :inline="true"
      :data-testid="`${name}-calendars`"
      auto-apply
      prevent-min-max-navigation
      @update:model-value="onUpdate"
    />
  </div>
</template>

<style scoped>
/* Size the inline calendars via vue-datepicker's CSS custom properties. Custom properties inherit
   into the library's own DOM even though it renders outside this component's scoped styles. */
/* The theme class re-declares every variable on the inner menu, so the overrides must reach it too */
.period-range-calendars,
.period-range-calendars :deep(.dp__theme_light) {
  /* Only the cell height: the width follows the column, see the cell rules below */
  --dp-cell-size: 30px;
  --dp-cell-padding: 1px;
  --dp-font-size: 14px;
  --dp-menu-padding: 6px;
  --dp-common-padding: 6px;
  --dp-menu-min-width: 0;
  /* The library paints the selected range with its hover grey, indistinguishable from disabled days */
  --dp-primary-color: #2563eb;
  --dp-range-between-dates-background-color: #dbeafe;
  --dp-range-between-dates-text-color: #1e3a8a;
  --dp-range-between-border-color: #dbeafe;
  --dp-disabled-color: transparent;
  --dp-disabled-color-text: #c4c8cf;
}

.period-range-calendars-container {
  container-type: inline-size;
}

/* Two months of 7 or 8 columns: below this width a 14px label no longer fits its column */
@container (max-width: 420px) {
  .period-range-calendars,
  .period-range-calendars :deep(.dp__theme_light) {
    --dp-cell-size: 26px;
    --dp-font-size: 12px;
  }
}

/* The library sizes the menu to its content: let it fill the popover and give each month an equal share */
.period-range-calendars :deep(.dp__outer_menu_wrap) {
  flex: 1;
  min-width: 0;
}

.period-range-calendars :deep(.dp__menu_inner) {
  gap: 12px;
}

.period-range-calendars :deep(.dp__instance_calendar) {
  flex: 1 1 0;
  min-width: 0;
}

/* Cells follow their column instead of a fixed width, so the months fill a wide popover without
   overflowing a narrow drawer; the cap keeps the day bubbles round on very wide screens */
.period-range-calendars :deep(.dp__calendar_header_item),
.period-range-calendars :deep(.dp__calendar_item) {
  flex: 1 1 0;
  min-width: 0;
  width: auto;
}

.period-range-calendars :deep(.dp__cell_inner) {
  width: 100%;
  max-width: 36px;
  margin: 0 auto;
}

/* The month grid gets an inline `width: var(--dp-menu-min-width)`, which is 0 here: fill the month instead */
.period-range-calendars :deep(.dp--overlay-relative) {
  width: 100% !important;
}

/* Month grids: the library paints in-between months with the hover grey and gives disabled ones no text color */
.period-range-calendars :deep(.dp__cell_in_between) {
  background: var(--dp-range-between-dates-background-color);
  color: var(--dp-range-between-dates-text-color);
}

.period-range-calendars :deep(.dp__overlay_cell_disabled) {
  color: var(--dp-disabled-color-text);
}
</style>
